/**
 * components/charts/DimBar — a horizontal bar chart for a one-dimension breakdown of
 * the learning aggregates, built to the dataviz skill:
 *
 * - `winrate` mode is a *magnitude* (0→100%) → sequential single hue (the accent). One
 *   series, so no legend box (the card title names it); a recessive 50% reference line.
 * - `avgR` mode is a *polarity* (above / below break-even) → diverging, centered on a
 *   0R baseline, growing profit-green to the right and loss-red to the left — the same
 *   owner-fixed poles as the rest of the terminal.
 *
 * Every bar carries its value at the tip and the sample count `n` (never a naked
 * percentage — a 100% winrate over n=1 is noise, and the reader must see that). Bars are
 * thin with a rounded data-end squared at the baseline; the track/reference lines are
 * recessive. Each row is a labelled group with a native tooltip for the full figures.
 */
import { pctPlain } from '../../lib/format';

export type DimMode = 'winrate' | 'avgR';

export interface DimDatum {
  readonly key: string;
  readonly label: string;
  /** winrate: fraction 0..1 · avgR: signed R multiple. */
  readonly value: number;
  readonly n: number;
}

interface DimBarProps {
  readonly data: readonly DimDatum[];
  readonly mode: DimMode;
}

function rMult(v: number): string {
  const s = Math.abs(v).toFixed(2);
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${s}R`;
}

export function DimBar({ data, mode }: DimBarProps): JSX.Element {
  if (data.length === 0) {
    return <div className="state"><span className="muted">No samples in this breakdown yet.</span></div>;
  }

  if (mode === 'winrate') {
    return (
      <div className="dbar" role="img" aria-label="Winrate by bucket">
        {data.map((d) => (
          <div className="dbar__row" key={d.key}>
            <span className="dbar__label" title={d.label}>{d.label}</span>
            <div className="dbar__track">
              <span className="dbar__ref" style={{ left: '50%' }} aria-hidden />
              <span
                className="dbar__fill dbar__fill--seq"
                style={{ width: `${Math.max(0, Math.min(1, d.value)) * 100}%` }}
                title={`${d.label}: ${pctPlain(d.value, 1)} winrate over ${d.n} trade${d.n === 1 ? '' : 's'}`}
              />
            </div>
            <span className="dbar__val num">
              {pctPlain(d.value, 0)} <span className="muted">n={d.n}</span>
            </span>
          </div>
        ))}
        <div className="dbar__axis">
          <span>0%</span><span>50%</span><span>100%</span>
        </div>
      </div>
    );
  }

  // avgR — diverging around 0R. Symmetric scale so left/right magnitudes are comparable.
  const max = Math.max(0.5, ...data.map((d) => Math.abs(d.value)));
  return (
    <div className="dbar dbar--div" role="img" aria-label="Average realized R by bucket">
      {data.map((d) => {
        const frac = Math.min(1, Math.abs(d.value) / max); // 0..1 of a half-track
        const pos = d.value >= 0;
        return (
          <div className="dbar__row" key={d.key}>
            <span className="dbar__label" title={d.label}>{d.label}</span>
            <div className="dbar__track">
              <span className="dbar__zero" aria-hidden />
              <span
                className={`dbar__fill dbar__fill--${pos ? 'pos' : 'neg'}`}
                style={pos ? { left: '50%', width: `${frac * 50}%` } : { right: '50%', width: `${frac * 50}%` }}
                title={`${d.label}: ${rMult(d.value)} avg realized over ${d.n} trade${d.n === 1 ? '' : 's'}`}
              />
            </div>
            <span className={`dbar__val num ${pos ? 'profit' : 'loss'}`}>
              {rMult(d.value)} <span className="muted">n={d.n}</span>
            </span>
          </div>
        );
      })}
      <div className="dbar__axis dbar__axis--div">
        <span>−{max.toFixed(1)}R</span><span>0</span><span>+{max.toFixed(1)}R</span>
      </div>
    </div>
  );
}
